import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule }  from 'primeng/button';

import { NotificationService } from './notification.service';
import { AppNotification, NotificationType } from './notification.types';

interface IconStyle { icon: string; classes: string; }

const ICON_BY_TYPE: Record<string, IconStyle> = {
  EmployeeOnboarded:       { icon: 'pi-user-plus',          classes: 'bg-green-pale dark:bg-green/15 text-green dark:text-green-light' },
  EmployeeAwaitingDetails: { icon: 'pi-clock',              classes: 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200' },
  PaymentFailed:           { icon: 'pi-exclamation-circle', classes: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300' },
  RemittanceFailed:        { icon: 'pi-exclamation-circle', classes: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300' },
  PayslipAvailable:        { icon: 'pi-file',               classes: 'bg-green-pale dark:bg-green/15 text-green dark:text-green-light' },
  BankingDetailsUpdated:   { icon: 'pi-building',           classes: 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300' },
  PayrollRunReminder:      { icon: 'pi-calendar',           classes: 'bg-green-pale dark:bg-green/15 text-green dark:text-green-light' },
  PAYROLL_RUN_COMPLETE:    { icon: 'pi-check-circle',       classes: 'bg-green-pale dark:bg-green/15 text-green dark:text-green-light' },
  PAYROLL_H1_COMPLETE:     { icon: 'pi-check',              classes: 'bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200' },
  PAYROLL_H2_COMPLETE:     { icon: 'pi-check-circle',       classes: 'bg-green-pale dark:bg-green/15 text-green dark:text-green-light' },
  PaymentMethodReady:           { icon: 'pi-check-circle',       classes: 'bg-green-pale dark:bg-green/15 text-green dark:text-green-light' },
  PaymentMethodFailed:          { icon: 'pi-exclamation-circle', classes: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300' },
  REMITTANCE_PENSION_DUE_SOON:  { icon: 'pi-clock',              classes: 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200' },
  REMITTANCE_PAYE_OVERDUE:      { icon: 'pi-exclamation-circle', classes: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300' },
  RemittanceDueSoon:            { icon: 'pi-clock',              classes: 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200' },
  RemittanceOverdue:            { icon: 'pi-exclamation-circle', classes: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300' },
  REMITTANCE_MARKED_DONE:       { icon: 'pi-check-circle',       classes: 'bg-green-pale dark:bg-green/15 text-green dark:text-green-light' },
  HEADCOUNT_PENSION_REQUIRED:   { icon: 'pi-users',              classes: 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200' },
  HEADCOUNT_ITF_REQUIRED:       { icon: 'pi-info-circle',        classes: 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300' },
  COMPLIANCE_TIER_UPGRADED:     { icon: 'pi-unlock',             classes: 'bg-green-pale dark:bg-green/15 text-green dark:text-green-light' }
};

@Component({
  selector: 'app-notifications-panel',
  standalone: true,
  imports: [CommonModule, SidebarModule, ButtonModule],
  templateUrl: './notifications-panel.component.html'
})
export class NotificationsPanelComponent {
  protected readonly service = inject(NotificationService);
  private   readonly router  = inject(Router);

  protected readonly hasItems = computed(() => this.service.items().length > 0);
  protected readonly canLoadMore = computed(() =>
    this.service.items().length > 0 && this.service.items().length < this.service.totalItems()
  );

  protected onVisibleChange(open: boolean): void {
    if (!open) { this.service.closePanel(); }
  }

  protected iconFor(type: NotificationType): IconStyle {
    return ICON_BY_TYPE[type] ?? { icon: 'pi-bell', classes: 'bg-line dark:bg-line-dark text-ink-mid dark:text-night-mid' };
  }

  protected onRowClick(n: AppNotification): void {
    if (!n.isRead) { this.service.markAsRead(n.id).subscribe(); }
    // Deep-link remittance notifications to the remittances center.
    const remittanceTypes = [
      'REMITTANCE_PENSION_DUE_SOON', 'REMITTANCE_PAYE_OVERDUE', 'REMITTANCE_MARKED_DONE',
      'RemittanceDueSoon', 'RemittanceOverdue'
    ];
    if (remittanceTypes.includes(n.type)) {
      this.service.closePanel();
      void this.router.navigateByUrl('/employer/remittances');
    }
    // Upcoming-payroll reminders deep-link to the run-payroll page.
    if (n.type === 'PayrollRunReminder') {
      this.service.closePanel();
      void this.router.navigateByUrl('/employer/payroll');
    }
    // Headcount and compliance tier upgrades also deep-link to relevant pages.
    if (n.type === 'HEADCOUNT_PENSION_REQUIRED' || n.type === 'HEADCOUNT_ITF_REQUIRED') {
      this.service.closePanel();
      void this.router.navigateByUrl('/employer/employees');
    }
    if (n.type === 'COMPLIANCE_TIER_UPGRADED') {
      this.service.closePanel();
      void this.router.navigateByUrl('/employer/remittances');
    }
  }

  protected onDelete(event: Event, n: AppNotification): void {
    event.stopPropagation();
    this.service.deleteNotification(n.id).subscribe();
  }

  protected markAllRead(): void {
    this.service.markAllAsRead().subscribe();
  }

  protected loadMore(): void { this.service.loadMore(); }

  /** "2 hours ago", "Yesterday", "3 days ago" — short relative-time formatter. */
  protected timeAgo(iso: string): string {
    if (!iso) { return ''; }
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) { return iso; }
    const diff = Math.max(0, Date.now() - then);
    const min  = Math.floor(diff / 60_000);
    if (min < 1)    { return 'Just now'; }
    if (min < 60)   { return `${min} min${min === 1 ? '' : 's'} ago`; }
    const hr = Math.floor(min / 60);
    if (hr < 24)    { return `${hr} hour${hr === 1 ? '' : 's'} ago`; }
    if (hr < 48)    { return 'Yesterday'; }
    const days = Math.floor(hr / 24);
    if (days < 7)   { return `${days} days ago`; }
    if (days < 30)  { return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'} ago`; }
    return new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}
